import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faClockRotateLeft,
    faHotel,
    faLocationDot,
} from '@fortawesome/pro-regular-svg-icons';
import axios from 'axios';
import { parse } from 'qs';
import {
    type dayChangerFunc,
    type LocationGroup,
    SearchFromErrorMessage,
    type UrlStringParams,
} from '../types';
import HotelPriceLoader from './HotelPriceLoader';
import { type HotelBlockGroup } from './types';
import ItinerarySearchForm from '@search/ItinerarySearchForm';
import {
    type DateSpanObject,
    PaxType,
    type PaxCount,
    type ReserveInfo,
} from '@utils/types';
import { ReserveType, VerifyPathError } from '@utils/itinerary';
import {
    LocationType,
    type HotelLocation,
    type HotelUrlParams,
    type HotelPathData,
    type HotelStar,
} from '@utils/hotel';
import {
    type GetHotelBlockListResponse,
    HotelApiApiHotelVisibleHotelBlockListGet,
    HotelSearchApiApiHotelsListGet,
} from '@clients/HotelApi';
import type HotelPackage from '@itinerary/hotel/HotelPackage';
import type { AnalyticsAttribute } from '@store/analytics';

const popularCities = [
    {
        code: '16',
        title: 'مشهد',
        subtitle: 'خراسان رضوی',
        englishTitle: 'mashhad',
        type: LocationType.City,
    },
    {
        code: '79',
        title: 'تهران',
        subtitle: 'تهران',
        englishTitle: 'tehran',
        type: LocationType.City,
    },
    {
        code: '44',
        title: 'اصفهان',
        subtitle: 'اصفهان',
        englishTitle: 'isfahan',
        type: LocationType.City,
    },
    {
        code: '17',
        title: 'یزد',
        subtitle: 'یزد',
        englishTitle: 'yazd',
        type: LocationType.City,
    },
    {
        code: '15',
        title: 'شیراز',
        subtitle: 'فارس',
        englishTitle: 'shiraz',
        type: LocationType.City,
    },
];

export class HotelSearchForm extends ItinerarySearchForm<HotelLocation> {
    userDestination: HotelLocation | null = null;
    lastSearches: HotelLocation[] = [];
    searchTerm: string | null = null;
    citiesSearchList: HotelLocation[] = [];
    hotelsSearchList: HotelLocation[] = [];
    fromDate: Date = new Date();
    // FIX: if it is okay
    // should init with value of next day of (fromDate) variable
    toDate: Date | null = new Date();
    landingPageStars?: HotelStar;
    override priceLoader: HotelPriceLoader | undefined = undefined;
    get lastSearchesWithIcons(): HotelLocation[] {
        return this.lastSearches.map(location => ({
            ...location,
            icon:
                location.type === LocationType.City
                    ? faClockRotateLeft
                    : faHotel,
        }));
    }

    setUserDestinationFromPackage(pkg: HotelPackage) {
        this.userDestination = {
            cityTitle: pkg.cityName,
            code: pkg.id!.toString(),
            englishTitle: pkg.hotelName,
            subtitle: pkg.cityTitle,
            title: pkg.title,
            type: LocationType.Hotel,
        };
    }

    override nextDay(callbackFunc: dayChangerFunc): void {
        const fromDateTomorrow = dayConfig
            .gregory(this.fromDate)
            .add(1, 'day')
            .startOf('day')
            .toDate();
        const toDateTomorrow = dayConfig
            .gregory(this.toDate)
            .add(1, 'day')
            .startOf('day')
            .toDate();
        this.fromDate = fromDateTomorrow;
        this.toDate = toDateTomorrow;
        const [url, query] = this.viewUrl.split('?');
        const queries = parse(query) as any;

        callbackFunc({
            path: url,
            query: queries,
        });
    }

    override prevDay(callbackFunc: dayChangerFunc): void {
        const fromDateYesterday = dayConfig
            .gregory(this.fromDate)
            .subtract(1, 'day')
            .startOf('day')
            .toDate();
        const toDateYesterday = dayConfig
            .gregory(this.toDate)
            .subtract(1, 'day')
            .startOf('day')
            .toDate();
        this.fromDate = fromDateYesterday;
        this.toDate = toDateYesterday;
        const [url, query] = this.viewUrl.split('?');
        const queries = parse(query) as any;

        callbackFunc({
            path: url,
            query: queries,
        });
    }

    override get searchReady(): boolean {
        return !!(this.fromDate && this.toDate && this.userDestination);
    }

    get hotelsAndCities(): {
        title: string;
        locations: HotelLocation[];
        icon?: IconDefinition;
    }[] {
        const list = [];
        if (!this.searchTerm) {
            if (this.lastSearchesWithIcons.length) {
                list.push({
                    title: 'جستجوهای اخیر',
                    locations: this.lastSearchesWithIcons,
                    ignoreGroupInSearch: true,
                });
            }
            list.push({
                title: 'شهرهای پر‌سفر',
                locations: popularCities,
                icon: faLocationDot,
            });
        } else {
            if (this.citiesSearchList.length) {
                list.push({
                    title: 'لیست شهرها',
                    locations: this.citiesSearchList,
                    icon: faLocationDot,
                });
            }
            if (this.hotelsSearchList.length) {
                list.push({
                    title: 'لیست هتل‌ها',
                    locations: this.hotelsSearchList,
                    icon: faHotel,
                });
            }
        }
        return list;
    }

    override get destination(): HotelLocation | null {
        return this.userDestination;
    }

    override get filteredDestinations(): LocationGroup[] {
        return this.hotelsAndCities;
    }

    override generateViewUrl(params: UrlStringParams<HotelUrlParams>): string {
        const query = new URLSearchParams();
        if (params.fromDate) {
            query.append(
                'fromDate',
                dayConfig.jalali(params.fromDate).format(formatter())
            );
        }
        if (params.toDate) {
            query.append(
                'toDate',
                dayConfig.jalali(params.toDate).format(formatter())
            );
        }
        if (params.adultCount && params.adultCount !== '1') {
            query.append('adultCount', params.adultCount);
        }
        if (params.childCount && params.childCount !== '0') {
            query.append('childCount', params.childCount);
        }
        if (params.roomCount && params.roomCount !== '1') {
            query.append('roomCount', params.roomCount);
        }
        const q = query.toString();
        return `/hotel/${params.cityName?.toLocaleLowerCase()}${
            params.hotelName ? `/${params.hotelName?.toLocaleLowerCase()}` : ''
        }${q ? `?${q}` : ''}`;
    }

    addUserDestinationToLast() {
        if (!this.userDestination) {
            return;
        }
        // Removing existing
        const existingIndex = this.lastSearches.findIndex(
            (l: HotelLocation) => l.code === this.userDestination!.code
        );
        if (existingIndex > -1) {
            this.lastSearches.splice(existingIndex, 1);
        }
        // Adding new item
        this.lastSearches.unshift(this.userDestination);
        // Checking for the limit
        if (this.lastSearches.length > 3) {
            this.lastSearches.pop();
        }
    }

    get roomCount() {
        const count = this.initPaxCounts.find(
            count => count.code === PaxType.Room
        );
        return count?.count || 1;
    }

    override saveSearch() {
        this.addUserDestinationToLast();
        localStorage.setItem(
            this.lastSearchKey,
            JSON.stringify({
                userDestination: this.userDestination,
                fromDate: this.fromDate.toISOString(),
                toDate: this.toDate?.toISOString(),
                adultCount: this.adultCount,
                childCount: this.childCount,
                infantCount: this.infantCount,
                roomCount: this.roomCount,
                lastSearches: this.lastSearchesWithIcons,
            })
        );
    }

    override getSearchUrl(): Promise<string> {
        const analyticsStore = useAnalyticsStore();
        const searchFormStore = useSearchFormStore();
        if (process.client) {
            analyticsStore.trackAll(
                'search_hotel',
                analyticsStore.searchResultsAttributes
            );
        }
        this.saveSearch();
        searchFormStore.firstReserveType = ReserveType.Hotel;

        searchFormStore.setReserveType(ReserveType.Hotel);
        return Promise.resolve(this.viewUrl);
    }

    override get capacityInfo(): {
        [key in PaxType]?: {
            count: number;
            min: number;
            max: number;
        };
    } {
        return {
            [PaxType.Adult]: {
                count: this.adultCount,
                min: 1,
                max: 9,
            },
            [PaxType.Child]: {
                count: this.childCount,
                min: 0,
                max: 9,
            },
        };
    }

    override initPaxCounts: PaxCount[] = [
        {
            code: PaxType.Adult,
            title: 'بزرگسال',
            subtitle: 'بزرگتر از ۱۲ سال',
            count: 1,
        },
        {
            code: PaxType.Child,
            title: 'کودک',
            subtitle: 'کوچکتر از ۱۲ سال',
            count: 0,
        },
    ];

    override get itinerarySearchDates() {
        return {
            start: this.fromDate,
            end: this.toDate,
        };
    }

    override get loadPriceEnabled(): boolean {
        return false;
    }

    override async loadPrices(payload?: { until: Date }) {
        try {
            if (
                !this.toDate ||
                !this.fromDate ||
                !this.userDestination ||
                this.userDestination.type === LocationType.Hotel
            ) {
                return;
            }

            const oldLoader = this.priceLoader;
            const newLoader = new HotelPriceLoader({
                from: this.userDestination!.code,
                adultCount: this.adultCount,
                childCount: this.childCount,
                roomCount: this.roomCount,
            });
            if (!oldLoader?.equals(newLoader)) {
                this.priceLoader = newLoader;
            }
            await this.priceLoader?.fetchPrices(payload?.until);
        } catch (e) {
            reportException(e);
        }
    }

    override get reserveInfo(): ReserveInfo {
        return {
            type: ReserveType.Hotel,
            title: 'هتل',
            nameE: 'هتلی',
            icon: faHotel,
        };
    }

    override async searchLocations(searchKeyword: string) {
        this.searchTerm = searchKeyword.replace('هتل', '').trim();
        if (!this.searchTerm) {
            this.isLoadingLocations = false;
            return;
        }
        try {
            this.isLoadingLocations = true;
            this.citiesSearchList = [];
            this.hotelsSearchList = [];
            const { data } = await HotelSearchApiApiHotelsListGet({
                searchTerm: this.searchTerm,
            });
            const hotels = data.hotels || [];
            const cities = data.cities || [];
            if (!hotels.length && !cities.length) {
                await this.advancedSearch({
                    token: searchKeyword,
                    target: 'destination',
                });
            } else {
                this.hotelsSearchList = hotels.map(hotel => ({
                    code: `${hotel.id}`,
                    title: hotel.name ?? '',
                    englishTitle: hotel.englishName ?? '',
                    subtitle: `${hotel.provinceName}${
                        hotel.provinceName !== hotel.cityName
                            ? `، ${hotel.cityName}`
                            : ''
                    }`,
                    type: LocationType.Hotel,
                    cityTitle: hotel.cityEnglishName ?? '',
                }));
                this.citiesSearchList = cities.map(city => ({
                    code: `${city.id}`,
                    englishTitle: city.englishTitle ?? '',
                    subtitle: '',
                    title: city.title ?? '',
                    type: LocationType.City,
                }));
            }
        } catch (e) {
            reportException(e);
        } finally {
            this.isLoadingLocations = false;
        }
    }

    override async advancedSearch({
        token,
    }: {
        token: string;
        target: 'destination';
    }) {
        if (this.isLoadingLocations) {
            return;
        }
        try {
            const { data } = await axios.get<{
                recommendation: {
                    results?: { title: string; id: string }[];
                };
            }>(`https://data.mrbilit.ir/recommend/hotel/${token}`);
            const citiesResults: HotelLocation[] =
                data.recommendation?.results
                    ?.filter(item => !item.id.includes('/'))
                    .map(item => ({
                        code: item.id,
                        englishTitle: item.id || '',
                        subtitle: '',
                        title: item.title || '',
                        type: LocationType.City,
                        fixed: true,
                    })) || [];
            const hotelResults: HotelLocation[] =
                data.recommendation?.results
                    ?.filter(item => item.id.includes('/'))
                    .map(item => ({
                        code: item.id.split('/')[1],
                        title: item.title || '',
                        englishTitle: item.id.split('/')[1] || '',
                        type: LocationType.Hotel,
                        cityTitle: item.id.split('/')[0] || '',
                        fixed: true,
                    })) || [];
            if (citiesResults.length) {
                this.citiesSearchList = citiesResults;
            }
            if (hotelResults.length) {
                this.hotelsSearchList = hotelResults;
            }
        } catch (e) {
            reportException(e);
        }
    }

    override setDestination(location: HotelLocation | null): void {
        this.userDestination = location;
    }

    override setItinerarySearchDates(val: DateSpanObject | null): void {
        this.fromDate = val?.start || this.fromDate || new Date();
        this.toDate = val?.end || null;
    }

    override get viewUrl() {
        if (this.userDestination?.type === LocationType.City) {
            return this.generateViewUrl({
                cityName: this.userDestination.englishTitle.toLowerCase(),
                fromDate: this.fromDate.toISOString(),
                toDate: this.toDate?.toISOString(),
                adultCount: this.adultCount.toString(),
                childCount: this.childCount.toString(),
                roomCount: this.roomCount.toString(),
            });
        } else if (this.userDestination?.type === LocationType.Hotel) {
            return this.generateViewUrl({
                cityName: this.userDestination.cityTitle!.toLowerCase(),
                hotelName: this.userDestination.englishTitle.toLowerCase(),
                fromDate: this.fromDate.toISOString(),
                toDate: this.toDate?.toISOString(),
                adultCount: `${this.adultCount}`,
                childCount: `${this.childCount}`,
                roomCount: `${this.roomCount}`,
            });
        } else {
            throw new Error('Location type not handled!');
        }
    }

    override loadLastSearch() {
        if (this.isLastSearchLoaded) {
            return;
        }
        const lastSearchParamsStr = localStorage.getItem(this.lastSearchKey);
        if (lastSearchParamsStr) {
            const lastSearchParams = JSON.parse(lastSearchParamsStr);
            this.lastSearches = lastSearchParams.lastSearches || [];
            this.userDestination = lastSearchParams.userDestination;
            let fromDate = new Date(
                lastSearchParams.fromDate?.date || lastSearchParams.fromDate
            );
            let toDate = new Date(
                lastSearchParams.toDate?.date || lastSearchParams.toDate
            );
            fromDate = dayConfig.gregory(fromDate).isAfter(new Date())
                ? fromDate
                : new Date();
            toDate = dayConfig.gregory(toDate).isAfter(fromDate)
                ? toDate
                : dayConfig.gregory(fromDate).add(1, 'day').toDate();
            this.fromDate = fromDate;
            this.toDate = toDate;
            if (
                lastSearchParams.adultCount === 0 ||
                lastSearchParams.adultCount
            ) {
                this.setPaxTypeCount({
                    type: PaxType.Adult,
                    count: lastSearchParams.adultCount,
                });
            }

            if (
                lastSearchParams.childCount === 0 ||
                lastSearchParams.childCount
            ) {
                this.setPaxTypeCount({
                    type: PaxType.Child,
                    count: lastSearchParams.childCount,
                });
            }

            if (
                lastSearchParams.infantCount === 0 ||
                lastSearchParams.infantCount
            ) {
                this.setPaxTypeCount({
                    type: PaxType.Infant,
                    count: lastSearchParams.infantCount,
                });
            }

            if (
                lastSearchParams.roomCount === 0 ||
                lastSearchParams.roomCount
            ) {
                this.setPaxTypeCount({
                    type: PaxType.Room,
                    count: lastSearchParams.roomCount,
                });
            }
        } else {
            // ssr and client side
            this.fromDate = dayConfig.gregory().toDate();
            this.toDate = dayConfig.gregory().add(1, 'day').toDate();
        }
        this.isLastSearchLoaded = true;
    }

    override parsePath(pathData: HotelPathData): HotelUrlParams {
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return {
            cityName: pathData.city,
            hotelName: pathData.hotelName,
            fromDate: pathData.fromDate
                ? dayConfig
                      .gregory(pathData.fromDate, {
                          jalali: true,
                          format: formatter(),
                      })
                      .format(formatter())
                : dayConfig.gregory(today).format(formatter()),
            toDate: pathData.toDate
                ? dayConfig
                      .gregory(pathData.toDate, {
                          jalali: true,
                          format: formatter(),
                      })
                      .format(formatter())
                : dayConfig.gregory(tomorrow).format(formatter()),
            adultCount: Number(pathData.adultCount) || 1,
            childCount: Number(pathData.childCount) || 0,
            roomCount: Number(pathData.roomCount) || 1,
            static: !!pathData.static,
            stars: pathData.stars as HotelStar | undefined,
        };
    }

    override async verifyViewPath(
        path: string,
        pathData: HotelPathData
    ): Promise<void> {
        await new Promise<void>((resolve, reject) => {
            const regex =
                /hotel\/([a-zA-Z][a-zA-Z0-9-]+)\/?([a-zA-Z][a-zA-Z0-9-]+)?\/?(\d{4}-\d{2}-\d{2})?\/?(\d{4}-\d{2}-\d{2})?\/?(\d)?\/?(\d)?\/?(\d)?(?:\/|$)?/;
            const params = regex.exec(path);
            const today = new Date();
            const tomorrow = new Date();
            tomorrow.setDate(today.getDate() + 1);
            if (params?.length && !!params[1]) {
                if (!params[3]) {
                    if (
                        !!params[2] &&
                        (params[1] !== params[1].toLowerCase() ||
                            params[2] !== params[2].toLowerCase())
                    ) {
                        return reject(
                            new VerifyPathError(
                                'Path not valid',
                                301,
                                true,
                                path.replace(regex, a => a.toLowerCase())
                            )
                        );
                    }
                    return resolve();
                } else {
                    return reject(
                        new VerifyPathError(
                            'Path not valid',
                            301,
                            true,
                            this.generateViewUrl({
                                cityName: params[1].toLowerCase(),
                                hotelName: params[2].toLowerCase(),
                                fromDate: params[3]
                                    ? dayConfig
                                          .gregory(params[3], {
                                              jalali: true,
                                              format: formatter(),
                                          })
                                          .toISOString()
                                    : today.toISOString(),
                                toDate: params[4]
                                    ? dayConfig
                                          .gregory(params[4], {
                                              jalali: true,
                                              format: formatter(),
                                          })
                                          .toISOString()
                                    : tomorrow.toISOString(),
                                adultCount: params[5] || '1',
                                childCount: params[6] || '0',
                                roomCount: params[7] || '1',
                            })
                        )
                    );
                }
            } else {
                return reject(new VerifyPathError('Path not valid', 404));
            }
        });
        let fromDate = pathData.fromDate
            ? parseDate(pathData.fromDate)!
            : new Date();
        const toDate = pathData.toDate
            ? parseDate(pathData.toDate)!
            : undefined;
        const newQueries: { fromDate?: string; toDate?: string } = {};
        const today = dayConfig.jalali();
        if (
            pathData.fromDate &&
            dayConfig.gregory(fromDate).isBefore(today, 'day')
        ) {
            newQueries.fromDate = dayConfig.jalali(today).format(formatter());
            fromDate = today.toDate();
        }
        if (pathData.toDate) {
            const dayAfterFromDate = dayConfig.gregory(fromDate).add(1, 'day');
            if (dayConfig.gregory(toDate).isBefore(dayAfterFromDate, 'day')) {
                newQueries.toDate = dayConfig
                    .jalali(dayAfterFromDate)
                    .format(formatter());
            }
        }
        if (Object.keys(newQueries).length) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { city, hotelName, ...query } = pathData;
            return Promise.reject(
                new VerifyPathError(
                    'queries not valid',
                    301,
                    true,
                    path,
                    parse({ ...query, ...newQueries }) as {
                        [key: string]: string;
                    },
                    true
                )
            );
        } else {
            return Promise.resolve();
        }
    }

    override async loadViewUrlParams(params: HotelUrlParams): Promise<void> {
        if (
            !this.userDestination ||
            this.userDestination.type === LocationType.Hotel
        ) {
            const cityName = params ? params.cityName : '';
            this.searchTerm = cityName;
            await this.searchLocations(cityName);
            const city = this.citiesSearchList.find(
                c => c.englishTitle.toUpperCase() === cityName.toUpperCase()
            );
            if (city) {
                this.userDestination = city;
            } else {
                throw new VerifyPathError('شهر مورد نظر یافت نشد.', 404);
            }
        }
        this.setPaxTypeCount({
            type: PaxType.Adult,
            count: params.adultCount || 1,
        });
        this.setPaxTypeCount({
            type: PaxType.Child,
            count: params.childCount || 0,
        });
        this.setPaxTypeCount({
            type: PaxType.Room,
            count: params.roomCount || 1,
        });
        if (!params.static) {
            this.fromDate = new Date(params.fromDate);
            this.toDate = new Date(params.toDate);
        } else {
            this.fromDate = new Date();
            this.toDate = null;
        }
        this.landingPageStars = params.stars;
        return Promise.resolve();
    }

    get nights(): number {
        return dayConfig
            .gregory(this.toDate)
            .startOf('day')
            .diff(dayConfig.gregory(this.fromDate).startOf('day'), 'day');
    }

    override get searchAttributes(): AnalyticsAttribute {
        const fromDate = dayConfig
            .gregory(this.fromDate ?? undefined)
            .set('hour', 12)
            .set('minute', 0)
            .set('second', 0)
            .toDate();

        const toDate = dayConfig
            .gregory(this.toDate ?? undefined)
            .set('hour', 12)
            .set('minute', 0)
            .set('second', 0)
            .toDate();
        let link = '';
        try {
            link = this.viewUrl;
        } catch (e) {
            reportException(e);
        }
        return {
            reserveType: ReserveType.Hotel,
            reserveTypeFa: this.reserveInfo.title,
            city:
                this.userDestination?.type === LocationType.City
                    ? this.userDestination?.title
                    : this.userDestination?.subtitle,
            hotel:
                this.userDestination?.type === LocationType.Hotel
                    ? this.userDestination?.title
                    : undefined,
            toName: this.userDestination?.title,
            fromDate: dayConfig.gregory(fromDate).toDate(),
            toDate: dayConfig.gregory(toDate).toDate(),
            fromDateString: dayConfig.gregory(fromDate).utc(true).toISOString(),
            toDateString: dayConfig.gregory(toDate).utc(true).toISOString(),
            fromDateFa: this.fromDate
                ? dayConfig.jalali(fromDate).format('dddd YYYY/MM/DD')
                : undefined,
            toDateFa: this.toDate
                ? dayConfig.jalali(toDate).format('dddd YYYY/MM/DD')
                : undefined,
            nights: `${this.nights}`,
            link,
        };
    }

    override verifySearchForm(currentUrl: string): Promise<void> {
        let error;
        if (!this.userDestination) {
            error = 'لطفا هتل یا شهر مقصد را انتخاب کنید';
        } else if (!this.fromDate || !this.toDate) {
            error = 'لطفا تاریخ ورود و خروج را انتخاب کنید';
        } else if (
            dayConfig.gregory(this.fromDate).isSame(this.toDate, 'day')
        ) {
            error = 'تاریخ ورود و خروج نمی‌تواند یکسان باشد';
        } else if (
            dayConfig.gregory(this.fromDate).isAfter(this.toDate, 'day')
        ) {
            error = 'تاریخ ورود نمی‌تواند قبل از تاریخ خروج باشد.';
        }
        if (error) {
            return Promise.reject(error);
        }

        if (currentUrl && this.duplicateViewPath(currentUrl)) {
            return Promise.reject(
                new Error(SearchFromErrorMessage.DuplicatePath)
            );
        }

        return Promise.resolve();
    }

    //#region blocking
    isLoadingHotelBlocking = true;
    hotelBlocking: GetHotelBlockListResponse[] = [];
    async fetchBlocking() {
        this.isLoadingHotelBlocking = true;
        try {
            const { data } = await HotelApiApiHotelVisibleHotelBlockListGet();
            this.hotelBlocking = data.hotelBlocks || [];
        } catch (e) {
            reportException(e);
        } finally {
            this.isLoadingHotelBlocking = false;
        }
    }

    get hotelBlocksGrouped() {
        return this.hotelBlocking.map(block => ({
            ...block,
            hotelBlockDetails: block.hotelBlockDetails?.reduce(
                (prev, current) => {
                    const exist = prev.find(
                        group => group.cityId === current.cityId
                    );
                    if (exist) {
                        exist.blocks = [...exist.blocks, current];
                        return prev;
                    }
                    return [
                        ...prev,
                        {
                            cityId: current.cityId || 0,
                            cityName: current.title || '',
                            blocks: [current],
                        },
                    ];
                },
                [] as HotelBlockGroup[]
            ),
        }));
    }

    //#endregion
}
